/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import {
	closeSnackbar,
	SnackbarProvider,
	type CustomContentProps
} from 'notistack';
import { forwardRef, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { IconClose } from '../../assets';
import IconButton from '@mui/material/IconButton';

const ToastComponent = forwardRef( ( props: CustomContentProps, ref ) => {
	const isSuccess = props.variant === 'success';
	const isWarning = props.variant === 'warning';
	const isError = props.variant === 'error';

	return (
		<Box
			ref={ ref } sx={ {
				backgroundColor: getBackgroundColor( isSuccess, isWarning, isError ),
				padding: '9px 16px',
				border: '1px solid',
				borderColor: getBorderColor( isSuccess, isWarning, isError ),
				borderRadius: '4px',
				display: 'flex',
				justifyContent: 'space-between',
				maxWidth: '400px',
				alignItems: 'center'
			} }
		>
			<Typography fontWeight={ 400 }>{ props.message }</Typography>
			<IconButton
				data-testid={ 'toast-close-button' }
				disableRipple={ true }
				onClick={ () => closeSnackbar( props.id ) }
				size={ 'small' }
			>
				<img width={ '14px' } height={ '14px' } src={ IconClose }/>
			</IconButton>
		</Box>
	);
} );

export function ToastProvider( { children }: { children: ReactNode } ): ReactNode {
	return (
		<SnackbarProvider
			Components={ {
				success: ToastComponent,
				warning: ToastComponent,
				error: ToastComponent,
				info: ToastComponent
			} }
		>
			{ children }
		</SnackbarProvider>
	);
}

function getBorderColor( isSuccess: boolean, isWarning: boolean, isError: boolean ) {
	if ( isSuccess ) {
		return 'success.light';
	}

	if ( isWarning ) {
		return 'warning.light';
	}

	if ( isError ) {
		return 'error.light';
	}

	return 'info.light';
}

function getBackgroundColor( isSuccess: boolean, isWarning: boolean, isError: boolean ) {
	if ( isSuccess ) {
		return 'success.100';
	}

	if ( isWarning ) {
		return 'warning.100';
	}

	if ( isError ) {
		return 'error.100';
	}

	return 'info.100';
}
